import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { doc, setDoc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';

interface Props {
  onConfigSaved: () => void;
}

export const RobinhoodConfig: React.FC<Props> = ({ onConfigSaved }) => {
  const { state } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const loadExistingConfig = async () => {
      if (!state.user) return;
      try {
        const apiCredentialsRef = collection(db, `users/${state.user.id}/api_credentials`);
        const robinhoodQuery = query(apiCredentialsRef, where("provider", "==", "robinhood"));
        const querySnapshot = await getDocs(robinhoodQuery);
        if (!querySnapshot.empty) {
          const credentialDoc = querySnapshot.docs[0];
          const credentialData = credentialDoc.data();
          if (credentialData) {
            setUsername(credentialData.username || '');
            if (credentialData.password) {
              setPassword('********'); // Masked password if exists
            } else {
              setPassword('');
            }
            return;
          }
        }
      } catch (e) {
        setError('Failed to load existing Robinhood credentials');
      }
    };
    loadExistingConfig();
  }, [state.user]);

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    if (!state.user) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }
    try {
      const apiCredentialsRef = collection(db, `users/${state.user.id}/api_credentials`);
      // Save/Upsert Robinhood credentials
      const robinhoodQuery = query(apiCredentialsRef, where("provider", "==", "robinhood"));
      const querySnapshot = await getDocs(robinhoodQuery);
      if (!querySnapshot.empty) {
        // Update existing credential doc
        const credentialDoc = querySnapshot.docs[0].ref;
        await setDoc(credentialDoc, { username, password }, { merge: true });
      } else {
        // Create new credential doc
        await setDoc(doc(apiCredentialsRef), { provider: "robinhood", username, password });
      }
      setSuccess(true);
      onConfigSaved();
    } catch (e) {
      setError('Failed to save Robinhood credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Robinhood Integration
        </Typography>
        <Box component="form" noValidate autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Robinhood Username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            fullWidth
            autoComplete="username"
            required
          />
          <TextField
            label="Robinhood Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            fullWidth
            autoComplete="current-password"
            required
            type={showPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">Robinhood credentials saved!</Alert>}
          <Button variant="contained" color="primary" onClick={handleSave} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save Credentials'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
